import { FormProvider, useForm } from 'react-hook-form';
import { Divider, Stack } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useState } from 'react';
import type {
  DowngradeReasonQuestion as DowngradeReasonQuestionType,
  FormResult,
} from '../../types';
import type { SurveyQuestion as SurveyQuestionModel } from '../../api/survey-model';
import { S } from './StyledFormComponents';
import { DowngradeExplanationsQuestion } from './DowngradeExplanationsQuestion';
import { DowngradeReasonQuestion } from './DowngradeReasonQuestion';
import { MoreDetailsQuestion } from './MoreDetailsQuestion';

interface Props {
  downgradeReason?: DowngradeReasonQuestionType;
  downgradeExplanations?: SurveyQuestionModel[];
  formId: string;
  onSubmit(data: FormResult): void;
}

export function ChurnSurveyForm({
  downgradeReason,
  downgradeExplanations,
  onSubmit,
  formId,
}: Props): JSX.Element {
  const methods = useForm<FormResult>({
    mode: 'onChange',
    defaultValues: {
      downgradeReason: '',
      downgradeExplanations: [],
      moreDetails: '',
    },
  });
  const { setValue, register, handleSubmit, errors, formState, getValues } = methods;
  const { isSubmitting } = formState;

  const { t } = useTranslationWithPrefix('checkout:churn.surveyv2');
  const [selectedDowngradeExplanationQuestion, setSelectedDowngradeExplanationQuestion] = useState<
    SurveyQuestionModel | undefined
  >(undefined);

  const hasAnExplanationSelected = (): boolean => {
    return (
      Array.isArray(getValues().downgradeExplanations) &&
      getValues().downgradeExplanations.some((answer) => answer.option !== '')
    );
  };

  return (
    <FormProvider {...methods}>
      <S.Form onSubmit={handleSubmit((data) => onSubmit(data))} id={formId}>
        <Stack preset="form">
          <DowngradeReasonQuestion
            downgradeExplanations={downgradeExplanations}
            downgradeReasonOptions={downgradeReason?.answers}
            inputRef={register({
              required: t('required-field').toString(),
            })}
            onChange={(selectedExplanationsGroup) => {
              setSelectedDowngradeExplanationQuestion(selectedExplanationsGroup);
              setValue('downgradeExplanations', []);
            }}
            error={errors.downgradeReason?.message}
            isDisabled={isSubmitting}
            isInvalid={'downgradeReason' in errors}
          />

          <Divider />

          <DowngradeExplanationsQuestion
            downgradeExplanation={selectedDowngradeExplanationQuestion}
            isDisabled={isSubmitting}
          />

          <Divider />

          <MoreDetailsQuestion
            isSubmitting={isSubmitting}
            isDisabled={!hasAnExplanationSelected()}
          />
        </Stack>
      </S.Form>
    </FormProvider>
  );
}
